import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { Modal, Button, Input, Group, Slider, Grid, Text, Switch, Alert } from '@mantine/core';
import { IconHelp, IconCheck, IconX, IconAlertCircle } from '@tabler/icons-react';
import ReactDOM from 'react-dom';
import TestsApi from "../../api/TestsApi";
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { SelectTooltip } from "../../components/GeneralComponent/GeneralComponent";

export function TrafficControl({ selectedMemberWebsite, pageId, isTrafficControlModalOpen, onTrafficControlClose }:any) {
  const navigate = useNavigate();
  const [valueTrafficControl, setValueTrafficControl] = useState(10);
  const [trafficControlValues, setTrafficControlValues] = useState([]);

  const trafficControlRef = useRef(null);
  const autoAdjustRef = useRef(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpTargetId, setHelpTargetId] = useState(0);
  const [tooltipPosition, setTooltipPosition] = useState({});
  const [autoAdjust, setAutoAdjust] = useState(true);

  useEffect(() => {
    if (isTrafficControlModalOpen) {
      if (!pageId) {
        navigate('/review-tests');
        return;
      }
      TestsApi.gettrafficcontrol({
        page_id:pageId
      })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          if (response.status === 401 || response.status === 400) {
            navigate("/logout");
          }
        }
      })
      .then(data => {
        if (data.status === 1) {
          const transformedData = data.data.test_row_data.map(test => ({
            variableId: test.test_id,
            value: parseFloat(test.test_value),
          }));
          const controlValue = Math.max(10, Math.min(90, parseFloat(data.data.control)));
          setValueTrafficControl(controlValue);
          setTrafficControlValues(transformedData);
          setAutoAdjust(data.data.auto_adjust);
        } else {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message,
          });
          onTrafficControlClose();
        }
      })
      .catch(error => {
        console.error("API Error:", error);
      });
    }
  }, [isTrafficControlModalOpen, pageId]);

  const handleTrafficControlResult = useCallback(() => {
    const remainingTraffic = 100 - valueTrafficControl;
    const newValues = trafficControlValues.map((item) => ({
      ...item,
      value: parseFloat((remainingTraffic / trafficControlValues.length).toFixed(2)),
    }));
    setTrafficControlValues(newValues);
  }, [valueTrafficControl, trafficControlValues.length,trafficControlValues ]);

  useEffect(() => {
    const updateValuesOnMouseUp = () => {
      handleTrafficControlResult();
    };
    window.addEventListener('mouseup', updateValuesOnMouseUp);
    return () => {
      window.removeEventListener('mouseup', updateValuesOnMouseUp);
    };
  }, [handleTrafficControlResult]);

  const handleSaveTrafficControl = (e) => {
    e.preventDefault();
    if (autoAdjust) {
      modals.open({
        trapFocus: false,
        title: 'Message',
        children: (
        <>
          <Text size="sm" style={{ whiteSpace: "pre-line" }}>
            Auto Adjust is enabled. Your traffic data will be balanced automatically.
          </Text>
          <Group position="right" mt="sm">
            <Button onClick={() => {modals.closeAll();saveTrafficControlData();}}>OK</Button>
          </Group>
        </>
        ),
      });
      return;
    }
    // Turning Auto Adjust off needs an explicit confirmation (client request, Oct 2 wording)
    modals.open({
      trapFocus: false,
      title: 'Turn off Auto Adjust?',
      children: (
      <>
        <Text size="sm">
          Auto-adjust keeps your traffic split on target. With it off, the split can drift and results may take longer. Turn off anyway?
        </Text>
        <Group position="right" mt="sm">
          <Button variant="default" onClick={() => {modals.closeAll();saveTrafficControlData();}}>Turn off</Button>
          <Button onClick={() => {modals.closeAll();setAutoAdjust(true);}}>Cancel</Button>
        </Group>
      </>
      ),
    });
  }

  const saveTrafficControlData = () => {
    TestsApi.savetrafficcontrol({
      system_website_id: selectedMemberWebsite,
      page_id:pageId,
      control:valueTrafficControl,
      auto_adjust: autoAdjust ? 1 : 0
    })
    .then(response => {
      if (response.ok) {
        return response.json();
      } else {
        if (response.status === 401 || response.status === 400) {
          navigate("/logout");
        }
      }
    })
    .then(data => {
      if(data.status === 1){
        notifications.show({
          color: 'green',
          icon: <IconCheck size="1.1rem" />,
          title: 'Success',
          message: data.message,
        });
        onTrafficControlClose();
      } else {
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: data.message,
        });
        onTrafficControlClose();
      }
    })
    .catch(error => {
      console.error("API Error:", error);
    });
  }

  const handleHelpClick = (id:any) => {
    setShowHelpModal(true);
    setHelpTargetId(id);
    const refMapping = {
      206: trafficControlRef,
      352: autoAdjustRef
    };
    let iconRef = refMapping[id];
    if (iconRef.current) {
      const { top, left, width } = iconRef.current.getBoundingClientRect();
      const scrollOffset = window.scrollY;
      setTooltipPosition({
        [id]: { top: top + scrollOffset, left: left + width + 10 },
      });
    }
  };

  return (
    <>
      {showHelpModal && Object.keys(tooltipPosition).map((key) => (
        ReactDOM.createPortal(
          <div>
            <SelectTooltip
              isVisible={showHelpModal}
              setIsVisible={setShowHelpModal}
              id={helpTargetId}
              tooltipPosition={tooltipPosition[key]} 
            />
          </div>,
        document.body
      )))}
      <Modal.Root opened={isTrafficControlModalOpen} onClose={onTrafficControlClose} size="70%" trapFocus={false}>
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title>TRAFFIC CONTROL <IconHelp ref={trafficControlRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={() => handleHelpClick(206)}/></Modal.Title>
            <Modal.CloseButton />
          </Modal.Header>
          <Modal.Body>
            <form onSubmit={handleSaveTrafficControl}>
              <Grid mt={20}>
                <Grid.Col span={3}></Grid.Col>
                <Grid.Col span={1}>All Tests</Grid.Col>
                <Grid.Col span={4} mt={6}>
                  <Slider
                    value={valueTrafficControl}
                    onChange={(value) => {
                      setValueTrafficControl(value);
                    }}
                    min={10}
                    max={90}
                  />
                </Grid.Col>
                <Grid.Col span={1}>Control</Grid.Col>
                <Grid.Col span={3}></Grid.Col>
              </Grid>
              <Text mt={10} mb={20}>Percentage of traffic routed to:</Text>
              <Group>
                <Text style={{ display: 'flex'}}>Control:<Input w={80} size="xs" value={valueTrafficControl} mr={5} ml={5} disabled/>%</Text>
                {trafficControlValues.map((item, index) => (
                  <Text key={index} style={{ display: 'flex' }} ml={10} w={parseInt(item.variableId) >= 10 ? 160 : 150}>
                    Test {item.variableId}:
                    <Input w={80} size="xs" value={item.value} mr={5} ml={5} disabled />%
                  </Text>
                ))}
              </Group>
              <Group position="left" mt={15}>
                <Text>Auto Adjust: </Text>
                <Switch
                  checked={autoAdjust}
                  size="md"
                  onLabel="ON"
                  offLabel="OFF"
                  onChange={(event) => setAutoAdjust(event.currentTarget.checked)} 
                />
                <IconHelp ref={autoAdjustRef} size="1.1rem" color="red" style={{marginBottom:-3, cursor: 'pointer'}} onClick={() => handleHelpClick(352)} />
              </Group>
              <Group position="left" mt={15}>
                <Alert icon={<IconAlertCircle size="1rem" />} title="Traffic Balancer Notice" color="green">
                  Visiopt uses a fast variation delivery algorithm that prioritizes speed over real-time balancing.
                  <br />
                  Traffic distribution is automatically corrected by the <b>hourly traffic balancer</b>, ensuring accurate allocation across test variations.
                  <br />
                  Short-term imbalances are expected and do not affect test results.
                </Alert>
              </Group>
              <Group position="right">
                <Button type="submit" size="sm" w={80} mt={25}>
                  Save
                </Button>
              </Group>
            </form>
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  );
}